import React, { useContext } from 'react'
import moment from 'moment'
import 'moment/locale/es'
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import { Dialog, DialogContent, DialogTitle, Link, Chip } from '@material-ui/core'
import EntityContext from '../../../contexts/Entity'
import { EntityContextType } from '../../../schemas/Entity'
import { usePlanStatus } from '../../../hooks/usePlanStatus'

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER ?? ''
const WHATSAPP_URL = WHATSAPP_NUMBER ? `https://wa.me/${WHATSAPP_NUMBER}` : '#'

type Variant = 'expiring' | 'expired'

const variantConfig = {
  expiring: {
    title: 'Tu plan está próximo a vencer',
    badge: { bg: '#fffde7', color: '#f57f17', border: '#ffe082' },
  },
  expired: {
    title: 'Tu plan ha vencido',
    badge: { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' },
  },
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingRight: theme.spacing(1),
    },
    content: {
      padding: theme.spacing(2),
    },
    chip: (props: { variant: Variant }) => ({
      backgroundColor: variantConfig[props.variant].badge.bg,
      color: variantConfig[props.variant].badge.color,
      border: `1px solid ${variantConfig[props.variant].badge.border}`,
      fontWeight: 500,
      height: 'auto',
      padding: theme.spacing(1, 1.5),
      whiteSpace: 'normal' as const,
      '& .MuiChip-label': {
        whiteSpace: 'normal' as const,
        overflow: 'visible',
      },
    }),
    link: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      marginTop: theme.spacing(2),
      fontWeight: 600,
      color: '#25D366',
    },
    whatsappIcon: {
      width: 20,
      height: 20,
    },
  })
)

interface Props {}

export default function PlanStatusModal(_props: Props) {
  const { entityContext } = useContext(EntityContext) as EntityContextType
  const { isExpired, isExpiringSoon, daysUntilExpiry, deactivationDate } = usePlanStatus(entityContext.planExpiredAt)
  const expiryDateObj = entityContext.planExpiredAt ? new Date(entityContext.planExpiredAt) : new Date()

  const variant: Variant | null = isExpired ? 'expired' : isExpiringSoon ? 'expiring' : null

  const classes = useStyles({ variant: variant ?? 'expired' })
  const [open, setOpen] = React.useState(true)

  if (!variant) return null

  const { title } = variantConfig[variant]
  const formattedExpiry = moment(expiryDateObj).locale('es').format('D [de] MMMM [de] YYYY')

  const chipLabel = variant === 'expiring'
    ? (
      <>
        Tu plan vence en{' '}
        <strong>{daysUntilExpiry} {daysUntilExpiry === 1 ? 'día' : 'días'}</strong>{' '}
        ({formattedExpiry}). Renuévalo para continuar usando el sistema sin interrupciones.
      </>
    )
    : (
      <>
        Tu plan venció el <strong>{formattedExpiry}</strong>.{' '}
        El acceso al sistema será inactivado el{' '}
        <strong>{moment(deactivationDate).locale('es').format('D [de] MMMM [de] YYYY')}</strong>{' '}
        (15 días tras el vencimiento). Renueva tu plan para evitar la inactivación.
      </>
    )

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Chip className={classes.chip} label={chipLabel} />
        {WHATSAPP_NUMBER && (
          <Link href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={classes.link}>
            <img src={`${process.env.PUBLIC_URL || ''}/whatsapp.png`} alt="WhatsApp" className={classes.whatsappIcon} />
            Contactar para renovar
          </Link>
        )}
      </DialogContent>
    </Dialog>
  )
}
